import { useEffect, useRef, useState, type DragEvent } from 'react'
import partition from 'lodash/partition'
import uniqBy from 'lodash/uniqBy'
import { FileText, Pencil, Trash2, Upload } from 'lucide-react'
import { FILE_BASE_NAME_MAX_LENGTH, cleanFileBaseName, splitFileName } from '@klear/upload/file-name'
import { Button } from '@klear/ui/components/button'
import { Input } from '@klear/ui/components/input'
import { cn } from '@klear/ui/lib/utils'

type FileDropZoneProps = {
  id?: string
  files: File[]
  onFilesChange: (files: File[]) => void
  accept: readonly string[]
  acceptedLabel: string
  acceptedNote?: string
  disabled?: boolean
}

function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`
}

export function FileDropZone({
  id,
  files,
  onFilesChange,
  accept,
  acceptedLabel,
  acceptedNote,
  disabled = false
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [rejected, setRejected] = useState<File[]>([])

  const addFiles = (incoming: File[]) => {
    const [accepted, refused] = partition(incoming, (file) =>
      accept.some((entry) => (entry.startsWith('.') ? file.name.toLowerCase().endsWith(entry) : entry === file.type))
    )
    setRejected(refused)
    if (accepted.length > 0) onFilesChange(uniqBy([...files, ...accepted], fileKey))
  }

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (!disabled) setDragging(true)
  }

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return
    setDragging(false)
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    if (!disabled) addFiles([...event.dataTransfer.files])
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={cn(
          'flex flex-col items-center gap-2 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors',
          dragging ? 'border-primary bg-accent' : 'border-input',
          disabled && 'opacity-50'
        )}
      >
        <Upload className="h-6 w-6 text-muted-foreground" aria-hidden />
        <p className="text-base font-medium">Drop files here</p>
        <p className="text-sm text-muted-foreground">{acceptedLabel}</p>
        {acceptedNote !== undefined && <p className="text-xs text-muted-foreground">{acceptedNote}</p>}
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => inputRef.current?.click()}>
          Choose files
        </Button>
        <input
          ref={inputRef}
          id={id}
          type="file"
          multiple
          accept={accept.join(',')}
          disabled={disabled}
          className="hidden"
          onChange={(event) => {
            addFiles([...(event.target.files ?? [])])
            event.target.value = ''
          }}
        />
      </div>
      {rejected.length > 0 && (
        <p className="text-sm text-destructive [overflow-wrap:anywhere]">
          Not added, because Klear takes only {acceptedLabel}: {rejected.map((file) => file.name).join(', ')}.
        </p>
      )}
      {files.length > 0 && (
        <ul className="rounded-md border px-3">
          {files.map((file, index) => (
            <FileListRow
              key={`${fileKey(file)}:${index}`}
              file={file}
              disabled={disabled}
              onRename={(renamed) => {
                setRejected([])
                onFilesChange(files.map((other, i) => (i === index ? renamed : other)))
              }}
              onRemove={() => {
                setRejected([])
                onFilesChange(files.filter((_, i) => i !== index))
              }}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function renamedFile(file: File, name: string): File {
  return new File([file], name, { type: file.type, lastModified: file.lastModified })
}

function FileListRow({
  file,
  disabled,
  onRename,
  onRemove
}: {
  file: File
  disabled: boolean
  onRename: (file: File) => void
  onRemove: () => void
}) {
  const { base, extension } = splitFileName(file.name)
  const [draft, setDraft] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const discardRef = useRef(false)
  const editing = draft !== null

  useEffect(() => {
    if (!editing) return
    inputRef.current?.focus()
    inputRef.current?.select()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.target !== inputRef.current) return
      event.stopPropagation()
      event.preventDefault()
      discardRef.current = true
      setDraft(null)
    }
    window.addEventListener('keydown', onKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
  }, [editing])

  const save = () => {
    if (draft === null || discardRef.current) return
    setDraft(null)
    const typedExtension = extension !== '' && draft.toLowerCase().endsWith(extension.toLowerCase())
    const cleaned = cleanFileBaseName(typedExtension ? draft.slice(0, -extension.length) : draft)
    if (cleaned !== '' && cleaned !== base) onRename(renamedFile(file, `${cleaned}${extension}`))
  }

  return (
    <li className="flex items-center gap-3 border-b py-2 text-sm last:border-b-0">
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      {editing ? (
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <Input
            ref={inputRef}
            value={draft}
            maxLength={FILE_BASE_NAME_MAX_LENGTH}
            aria-label={`Name for ${file.name}`}
            size="sm"
            containerClassName="min-w-0 flex-1"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={save}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              event.currentTarget.blur()
            }}
          />
          {extension !== '' && <span className="shrink-0 text-muted-foreground">{extension}</span>}
        </div>
      ) : (
        <>
          <span className="min-w-0 flex-1 truncate">{file.name}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Rename ${file.name}`}
            disabled={disabled}
            onClick={() => {
              discardRef.current = false
              setDraft(base)
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Remove ${file.name}`}
        disabled={disabled}
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  )
}
